import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { 
  Shield, Cpu, Code, Cloud, Activity, Zap, 
  ArrowRight, ChevronRight, Lock, Server, 
  Wifi, Database, Thermometer, Pill
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Shield, Cpu, Code, Cloud, Activity, Zap,
  Lock, Server, Wifi, Database
};

const stats = [
  { value: 'TrackOtic', label: 'Flagship Product' },
  { value: '2-8°C', label: 'Cold Chain Monitoring' },
  { value: '24/7', label: 'Real-time Tracking' },
  { value: '100%', label: 'Compliance Ready' },
];

const trustedBy = [
  'Pharma Partners', 'Healthcare Logistics', 'Cold Chain Solutions'
];

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      await axios.post(`${API}/seed-data`).catch(() => {});
      
      const [servicesRes, studiesRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/case-studies`)
      ]);
      setServices(servicesRes.data.services.slice(0, 6));
      setCaseStudies(studiesRes.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  return (
    <div data-testid="home-page" className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden gradient-hero">
        <div className="absolute inset-0 grid-pattern opacity-50" />

        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Badge 
                className="mb-6 bg-blue-100 text-blue-700 border-blue-200 font-medium"
                data-testid="hero-badge"
              >
                INTELLIGENT SYSTEMS / SECURE SOLUTIONS
              </Badge>
              
              <h1 
                className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6"
                data-testid="hero-title"
              >
                Building the
                <span className="block text-primary">Future of Tech</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-xl">
                From IoT infrastructure to enterprise security, we engineer intelligent 
                systems that protect, monitor, and empower your digital transformation.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg"
                    className="bg-primary hover:bg-blue-700 text-white font-semibold h-14 px-8 rounded-lg shadow-primary"
                    data-testid="hero-cta-primary"
                  >
                    Start Your Project
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/case-studies">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-2 border-gray-300 text-gray-700 hover:border-primary hover:text-primary h-14 px-8 rounded-lg"
                    data-testid="hero-cta-secondary"
                  >
                    View Our Work
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-8 border-t border-gray-200">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <div className="font-heading text-3xl md:text-4xl font-bold text-primary">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Hero Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative aspect-square">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <div className="w-32 h-32 bg-primary rounded-2xl flex items-center justify-center shadow-primary">
                      <Shield className="w-16 h-16 text-white" />
                    </div>
                  </div>
                </div>
                
                {[Cpu, Cloud, Lock, Database, Wifi, Server].map((Icon, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-14 h-14 bg-white rounded-xl shadow-soft border border-gray-100 flex items-center justify-center"
                    style={{
                      top: `${50 + 38 * Math.sin((i * Math.PI * 2) / 6)}%`,
                      left: `${50 + 38 * Math.cos((i * Math.PI * 2) / 6)}%`,
                      transform: 'translate(-50%, -50%)'
                    }}
                    animate={{
                      y: [0, -8, 0],
                    }}
                    transition={{
                      duration: 3,
                      delay: i * 0.2,
                      repeat: Infinity,
                    }}
                  >
                    <Icon className="w-6 h-6 text-gray-600" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Product - TrackOtic */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700" data-testid="featured-product-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-white/20 text-white border-white/30">
                FLAGSHIP PRODUCT
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                TrackOtic™
              </h2>
              <p className="text-xl text-blue-100 font-medium mb-4">
                Cold Chain Compliance Platform
              </p>
              <p className="text-blue-100 leading-relaxed mb-6">
                Ensure secure, compliant, and auditable delivery of vaccines, biologics, and 
                temperature-sensitive medications with real-time IoT monitoring and regulatory-grade documentation.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {['GDP Compliant', 'Real-time Tracking', 'Cold Chain', 'Audit Ready'].map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-white/10 text-white text-sm rounded-full border border-white/20">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex gap-4">
                <Link to="/products/trackotic">
                  <Button 
                    size="lg"
                    className="bg-white text-primary hover:bg-gray-100 font-semibold h-12 px-6 rounded-lg"
                    data-testid="explore-trackotic-btn"
                  >
                    Explore TrackOtic
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Pill className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">TrackOtic Dashboard</div>
                    <div className="text-xs text-gray-500">Pharma Distribution</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-primary">99.9%</div>
                    <div className="text-xs text-gray-600">Compliance Rate</div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-green-600">4.2°C</div>
                    <div className="text-xs text-gray-600">Avg Temperature</div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
                  <Thermometer className="w-6 h-6 text-blue-500" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-700">Cold Chain Status</div>
                    <div className="text-xs text-green-600">All systems normal</div>
                  </div>
                  <Badge className="bg-green-100 text-green-700">Optimal</Badge>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted By */}
      <section className="py-12 bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-sm text-gray-500 mb-8">
            Trusted by Industry Leaders
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {trustedBy.map((company) => (
              <span 
                key={company}
                className="font-heading text-lg md:text-xl text-gray-400 hover:text-gray-600 transition-colors"
              >
                {company}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
              CAPABILITIES
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-gray-600 max-w-2xl">
              Comprehensive technology solutions designed to secure, optimize, and transform your operations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Shield;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                    <IconComponent className="w-6 h-6 text-primary group-hover:text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {service.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {service.features.slice(0, 3).map((feature) => (
                      <span 
                        key={feature}
                        className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link to="/services">
              <Button 
                variant="outline" 
                className="border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
                data-testid="view-all-services-btn"
              >
                Explore All Services
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-gray-50" data-testid="case-studies-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
              SUCCESS STORIES
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Case Studies
            </h2>
            <p className="text-gray-600 max-w-2xl">
              Real-world implementations showcasing our expertise across industries.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-6">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-xl overflow-hidden shadow-soft border border-gray-100 hover:shadow-lg transition-shadow"
                data-testid={`case-study-card-${index}`}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={study.image_url} 
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <Badge className="absolute bottom-4 left-4 bg-white/90 text-primary border-0">
                    {study.category}
                  </Badge>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {study.description}
                  </p>
                  <div className="flex items-center text-sm text-gray-500">
                    <span>{study.industry}</span>
                    <span className="mx-2">•</span>
                    <span>{study.client_name}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/case-studies">
              <Button 
                variant="outline" 
                className="border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
                data-testid="view-all-case-studies-btn"
              >
                View All Case Studies
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Transform
              <span className="text-primary"> Your Infrastructure?</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Let&apos;s discuss how SentinelX can help secure and optimize your technology stack.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-blue-700 text-white font-semibold h-14 px-8 rounded-lg shadow-primary"
                  data-testid="cta-contact-btn"
                >
                  Schedule a Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/register">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:border-primary hover:text-primary h-14 px-8 rounded-lg"
                  data-testid="cta-register-btn"
                >
                  Create Free Account
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
